/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import { AdminManagerInputType } from 'features/signup';
import {
  AddManagerButton,
  Banner,
  Body,
  CheckIcon,
  ChoiceBox,
  ChoiceGrid,
  DarkButton,
  ErrorText,
  Field,
  FieldCaption,
  Helper,
  InlineRow,
  Label,
  ManagerError,
  ManagerInput,
  ManagerRemove,
  ManagerRow,
  Req,
  TextInput,
} from 'features/signup/ui/mobileStyles';
import { Controller, useFormContext } from 'react-hook-form';
import { execDaumPostcode } from 'shared/lib';

const Languages: Record<string, string> = {
  ENGLISH: '영어',
  CHINESE: '중국어',
  JAPANESE: '일본어',
  VIETNAMESE: '베트남어',
};

const MainListings: Record<string, string> = {
  ROOM: '원룸/투룸',
  OFFICETEL: '오피스텔',
  APARTMENT: '아파트',
  SHAREHOUSE: '셰어하우스',
  OTHERS: '기타',
};

/* ===================== 1/3 중개사무소/숙박업소 정보 ===================== */
export function RegStep1({ loginType }: { loginType: string | null }) {
  const methods = useFormContext();
  const { register, watch, setValue } = methods;
  const errors = methods.formState.errors as Record<string, any>;
  const agencyType = watch('agencyType');
  const mainListings: string[] = watch('mainListings') || [];

  const handleAddressSearch = () => {
    execDaumPostcode((address: string, addressEN: string) => {
      setValue('address', address);
      setValue('addressEN', addressEN);
    });
  };

  const toggleListing = (key: string) => {
    const cur: string[] = methods.getValues('mainListings') || [];
    setValue('mainListings', cur.includes(key) ? cur.filter((v) => v !== key) : [...cur, key]);
  };

  return (
    <Body>
      {/* 라디오: 중개사무소 / 숙박업소 */}
      <Field>
        <ChoiceGrid>
          <ChoiceBox type="button" $on={agencyType === 'ESTATE'} onClick={() => setValue('agencyType', 'ESTATE')}>
            <CheckIcon $on={agencyType === 'ESTATE'} />
            중개사무소
          </ChoiceBox>
          <ChoiceBox
            type="button"
            $on={agencyType === 'ACCOMMODATION'}
            onClick={() => setValue('agencyType', 'ACCOMMODATION')}
          >
            <CheckIcon $on={agencyType === 'ACCOMMODATION'} />
            숙박업소
          </ChoiceBox>
        </ChoiceGrid>
      </Field>

      <Field>
        <Label>
          중개사무소 혹은 숙박업소명(국문)
          <Req>*</Req>
        </Label>
        <TextInput placeholder="국문으로 입력해주세요" {...register('agencyName')} $invalid={!!errors.agencyName} />
        {errors.agencyName && <ErrorText>{errors.agencyName.message}</ErrorText>}
      </Field>

      <Field>
        <Label>
          중개사무소 혹은 숙박업소명(영문)
          <Req>*</Req>
        </Label>
        <TextInput placeholder="영문으로 입력해주세요" {...register('agencyNameEN')} $invalid={!!errors.agencyNameEN} />
        {errors.agencyNameEN && <ErrorText>{errors.agencyNameEN.message}</ErrorText>}
      </Field>

      {agencyType === 'ESTATE' && (
        <Field>
          <Label>
            부동산등록번호
            <Req>*</Req>
          </Label>
          <Controller
            name="registrationNumber"
            render={({ field }) => (
              <TextInput
                placeholder="등록번호를 입력해주세요"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value.replace(/[^0-9-]/g, ''))}
                $invalid={!!errors.registrationNumber}
              />
            )}
          />
          {errors.registrationNumber && <ErrorText>{errors.registrationNumber.message}</ErrorText>}
        </Field>
      )}

      <Field>
        <Label>
          주소(국문)
          <Req>*</Req>
        </Label>
        <InlineRow>
          <TextInput placeholder="국문으로 입력하세요" value={watch('address')} disabled $invalid={!!errors.address} />
          <DarkButton type="button" onClick={handleAddressSearch}>
            주소검색
          </DarkButton>
        </InlineRow>
        {errors.address && <ErrorText>{errors.address.message}</ErrorText>}
        <TextInput
          style={{ marginTop: 11 }}
          placeholder="상세주소를 입력해주세요"
          {...register('addressDetail')}
          $invalid={!!errors.addressDetail}
        />
        {errors.addressDetail && <ErrorText>{errors.addressDetail.message}</ErrorText>}
      </Field>

      <Field>
        <Label>
          대표 연락처
          <Req>*</Req>
        </Label>
        <Controller
          name="agencyPhoneNumber"
          render={({ field }) => (
            <TextInput
              inputMode="numeric"
              placeholder="-없이 입력해주세요"
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ''))}
              $invalid={!!errors.agencyPhoneNumber}
            />
          )}
        />
        {errors.agencyPhoneNumber && <ErrorText>{errors.agencyPhoneNumber.message}</ErrorText>}
        <Helper>매물 상세에 등록되어 고객이 볼 수 있는 연락처입니다.</Helper>
      </Field>

      <Field>
        <Label>주거래 매물</Label>
        <ChoiceGrid>
          {Object.entries(MainListings).map(([key, value]) => (
            <ChoiceBox key={key} type="button" $on={mainListings.includes(key)} onClick={() => toggleListing(key)}>
              <CheckIcon $on={mainListings.includes(key)} />
              {value}
            </ChoiceBox>
          ))}
        </ChoiceGrid>
      </Field>

      {/* 이메일 가입 시 비밀번호 (디자인엔 없으나 데스크탑 로직상 필수) */}
      {loginType === 'email' && (
        <>
          <Field>
            <Label>
              비밀번호
              <Req>*</Req>
            </Label>
            <TextInput
              type="password"
              placeholder="비밀번호를 입력하세요"
              {...register('password')}
              $invalid={!!errors.password}
            />
            {errors.password && <ErrorText>{errors.password.message}</ErrorText>}
          </Field>
          <Field>
            <Label>
              비밀번호 확인
              <Req>*</Req>
            </Label>
            <TextInput
              type="password"
              placeholder="비밀번호를 한 번 더 입력하세요"
              {...register('passwordConfirm')}
              $invalid={!!errors.passwordConfirm}
            />
            {errors.passwordConfirm && <ErrorText>{errors.passwordConfirm.message}</ErrorText>}
          </Field>
        </>
      )}
    </Body>
  );
}

/* ===================== 2/3 중개인 정보 ===================== */
export function RegStep2() {
  const methods = useFormContext();
  const { register, watch, setValue } = methods;
  const errors = methods.formState.errors as Record<string, any>;
  const languages: string[] = watch('languages') || [];

  const toggleLang = (key: string) => {
    const cur: string[] = methods.getValues('languages') || [];
    setValue('languages', cur.includes(key) ? cur.filter((v) => v !== key) : [...cur, key]);
  };

  return (
    <Body>
      <Field>
        <Label>
          성(국문)
          <Req>*</Req>
        </Label>
        <TextInput placeholder="국문으로 입력해주세요" {...register('lastName')} $invalid={!!errors.lastName} />
        {errors.lastName && <ErrorText>{errors.lastName.message}</ErrorText>}
      </Field>

      <Field>
        <Label>
          이름(국문)
          <Req>*</Req>
        </Label>
        <TextInput placeholder="국문으로 입력해주세요" {...register('firstName')} $invalid={!!errors.firstName} />
        {errors.firstName && <ErrorText>{errors.firstName.message}</ErrorText>}
      </Field>

      <Field>
        <Label>
          성(영문)
          <Req>*</Req>
        </Label>
        <TextInput placeholder="영문으로 입력해주세요" {...register('lastNameEN')} $invalid={!!errors.lastNameEN} />
        {errors.lastNameEN && <ErrorText>{errors.lastNameEN.message}</ErrorText>}
      </Field>

      <Field>
        <Label>
          이름(영문)
          <Req>*</Req>
        </Label>
        <TextInput placeholder="영문으로 입력해주세요" {...register('firstNameEN')} $invalid={!!errors.firstNameEN} />
        {errors.firstNameEN && <ErrorText>{errors.firstNameEN.message}</ErrorText>}
      </Field>

      <Field>
        <Label>
          연락처
          <Req>*</Req>
        </Label>
        <Controller
          name="phoneNumber"
          render={({ field }) => (
            <TextInput
              inputMode="numeric"
              placeholder="-없이 입력해주세요"
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ''))}
              $invalid={!!errors.phoneNumber}
            />
          )}
        />
        {errors.phoneNumber && <ErrorText>{errors.phoneNumber.message}</ErrorText>}
        <Helper>알림을 받을 연락처로 입력해주세요.</Helper>
      </Field>

      <Field>
        <Label>가능한 외국어</Label>
        <ChoiceGrid>
          {Object.entries(Languages).map(([key, value]) => (
            <ChoiceBox key={key} type="button" $on={languages.includes(key)} onClick={() => toggleLang(key)}>
              <CheckIcon $on={languages.includes(key)} />
              {value}
            </ChoiceBox>
          ))}
        </ChoiceGrid>
      </Field>
    </Body>
  );
}

/* ===================== 3/3 담당자 정보 ===================== */
export function RegStep3() {
  const methods = useFormContext();
  const adminManagerInfos = (methods.watch('adminManagerInfos') as AdminManagerInputType[]) || [];
  const showError = methods.formState.submitCount > 0;
  const managerErrors = methods.formState.errors.adminManagerInfos as
    | { adminManagerName?: { message?: string }; adminManagerPhoneNumber?: { message?: string } }[]
    | undefined;

  const isAddable = adminManagerInfos.length < 6;

  const addManager = () => {
    const arr = [...(methods.getValues('adminManagerInfos') || [])];
    if (arr.length >= 6) return;
    arr.push({ adminManagerId: null, adminManagerName: '', adminManagerPhoneNumber: '' });
    methods.setValue('adminManagerInfos', arr, { shouldValidate: false });
  };

  const removeManager = (idx: number) => {
    const arr = [...(methods.getValues('adminManagerInfos') || [])];
    arr.splice(idx, 1);
    methods.setValue('adminManagerInfos', arr, { shouldValidate: false });
  };

  const updateManager = (idx: number, key: 'adminManagerName' | 'adminManagerPhoneNumber', value: string) => {
    const arr = [...(methods.getValues('adminManagerInfos') || [])];
    arr[idx] = { ...arr[idx], [key]: value };
    methods.setValue('adminManagerInfos', arr, { shouldValidate: showError });
  };

  return (
    <Body>
      <Banner style={{ margin: '-24px -20px 24px' }}>
        대표 중개사님(또는 숙박업소 대표님) 외에 매물 등록과 가계약 신청을 관리할 담당자 연락처를 추가해주세요. 매물별
        담당자를 설정하고, 함께 알림을 받으실 수 있어요!
      </Banner>

      <FieldCaption>담당자 연락처(최대 6명)</FieldCaption>

      {adminManagerInfos.map(({ adminManagerName, adminManagerPhoneNumber }, idx) => (
        <div key={idx}>
          <ManagerRow>
            <ManagerInput
              type="text"
              placeholder="이름"
              value={adminManagerName || ''}
              onChange={(e) => updateManager(idx, 'adminManagerName', e.target.value)}
              $invalid={showError && !!managerErrors?.[idx]}
            />
            <ManagerInput
              type="text"
              inputMode="numeric"
              placeholder="연락처(-없이 입력)"
              value={adminManagerPhoneNumber || ''}
              onChange={(e) => updateManager(idx, 'adminManagerPhoneNumber', e.target.value.replace(/[^0-9]/g, ''))}
              $invalid={showError && !!managerErrors?.[idx]}
            />
            <ManagerRemove type="button" aria-label="삭제" onClick={() => removeManager(idx)}>
              −
            </ManagerRemove>
          </ManagerRow>
          {showError && managerErrors?.[idx] && <ManagerError>이름과 연락처를 모두 입력해주세요</ManagerError>}
        </div>
      ))}

      {isAddable && (
        <AddManagerButton type="button" onClick={addManager}>
          + 담당자 추가
        </AddManagerButton>
      )}
    </Body>
  );
}
